import styles from "./ToastContainer.module.scss";
import React, { useEffect, useRef } from "react";
import useToasts from "./useToasts.ts";
import { useTransitionMap } from "react-transition-state";
import { clsx } from "clsx";
import { useIntl } from "react-intl";

interface ExtraToastProps {
  onClose: () => void;
}

export default function ToastContainer({ targetId }: { targetId?: string }) {
  const { toasts, closeToast } = useToasts(targetId);
  const onUnmountRef = useRef(() => {});
  const transitioner = useTransitionMap<string>({
    allowMultiple: true,
    timeout: 500,
    initialEntered: true,
    onStateChange: (event) => {
      if (event.current.status === "exited") {
        transitioner.deleteItem(event.key);
        closeToast(event.key);
      }
    },
  });

  /**
   * Remove open toasts when the container unmounts, mostly for dialogs
   */
  useEffect(() => {
    onUnmountRef.current = () => {
      toasts.forEach((toast) => {
        closeToast(toast.id);
      });
    };
  }, [toasts, closeToast]);

  useEffect(() => {
    return () => onUnmountRef.current();
  }, []);

  useEffect(() => {
    toasts.forEach((toast) => {
      if (!transitioner.stateMap.get(toast.id)) {
        transitioner.setItem(toast.id);
      }
    });
  }, [transitioner, toasts]);

  const locale = useIntl().locale;

  return (
    // data-label is just to assist with finding it in the inspector
    // data-locale is a multi-language debugging convenience
    <ul
      data-label="toasts"
      className={styles.container}
      data-locale={locale}
      role="status"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => {
        const status = transitioner.stateMap.get(toast.id)?.status;
        return (
          <li
            className={clsx(
              styles.toastItem,
              (status === "entering" || status === "entered") &&
                styles.toastEnter
            )}
            key={toast.id}
          >
            {React.cloneElement<ExtraToastProps>(toast.toastElement, {
              onClose: () => {
                transitioner.toggle(toast.id, false);
              },
            })}
          </li>
        );
      })}
    </ul>
  );
}
